<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Asignación de Usuarios a Negocios
      </h1>
      <p class="text-gray-600 dark:text-gray-400">
        Gestiona qué usuarios tienen acceso a cada negocio del sistema
      </p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <!-- Panel izquierdo: Lista de Negocios -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Seleccionar Negocio
          </h2>
          <!-- Filtro de negocios -->
          <div class="relative">
            <input 
              v-model="negocioSearch" 
              type="text" 
              placeholder="Buscar negocio..." 
              class="w-full px-4 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            />
            <svg class="w-5 h-5 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
        </div>
        
        <div class="p-4 max-h-96 overflow-y-auto">
          <div v-if="loadingNegocios" class="text-center py-8">
            <svg class="animate-spin h-8 w-8 text-violet-600 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <p class="text-gray-500 mt-2">Cargando negocios...</p>
          </div>
          
          <div v-else-if="filteredNegocios.length === 0" class="text-center py-8 text-gray-500">
            No se encontraron negocios
          </div>
          
          <div v-else class="space-y-2">
            <button
              v-for="negocio in filteredNegocios"
              :key="negocio.id"
              @click="selectNegocio(negocio)"
              :class="[
                'w-full text-left p-4 rounded-lg border transition-all duration-200',
                selectedNegocio?.id === negocio.id
                  ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-300 dark:border-violet-600 text-violet-700 dark:text-violet-300'
                  : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
              ]"
            >
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="font-medium">{{ negocio.nombre }}</h3>
                  <p class="text-sm opacity-75">
                    <span :class="negocio.activo ? 'text-green-600' : 'text-red-600'">
                      {{ negocio.activo ? '● Activo' : '● Inactivo' }}
                    </span>
                    | ID: {{ negocio.id }}
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <span v-if="negocio.usuarios_count" class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full dark:bg-blue-900 dark:text-blue-300">
                    {{ negocio.usuarios_count }} usuarios
                  </span>
                  <svg v-if="selectedNegocio?.id === negocio.id" class="w-5 h-5 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Panel derecho: Gestión de Usuarios del Negocio -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
              Usuarios Asignados
            </h2>
            <button
              v-if="selectedNegocio"
              @click="showAddModal = true"
              class="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              Asignar Usuario
            </button>
          </div>
          
          <div v-if="selectedNegocio" class="bg-violet-50 dark:bg-violet-900/20 p-3 rounded-lg">
            <p class="text-sm text-violet-700 dark:text-violet-300">
              <strong>Negocio seleccionado:</strong> {{ selectedNegocio.nombre }}
            </p>
          </div>
        </div>

        <div class="p-6">
          <div v-if="!selectedNegocio" class="text-center py-12">
            <svg class="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m0 0h2M7 7h10M7 11h10M7 15h10"/>
            </svg>
            <p class="text-gray-500 text-lg">Selecciona un negocio</p>
            <p class="text-gray-400 text-sm mt-1">para gestionar sus usuarios asignados</p>
          </div>

          <div v-else>
            <div v-if="loadingUsuarios" class="text-center py-8">
              <svg class="animate-spin h-8 w-8 text-violet-600 mx-auto" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              <p class="text-gray-500 mt-2">Cargando usuarios...</p>
            </div>

            <div v-else-if="usuariosAsignados.length === 0" class="text-center py-8">
              <svg class="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
              </svg>
              <p class="text-gray-500">No hay usuarios asignados</p>
              <p class="text-gray-400 text-sm mt-1">Haz clic en "Asignar Usuario" para comenzar</p>
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="usuario in usuariosAsignados"
                :key="usuario.id"
                class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <div class="flex-1">
                  <div class="flex items-center gap-3">
                    <div>
                      <h3 class="font-medium text-gray-900 dark:text-white">{{ usuario.nombre }} {{ usuario.apellidoPaterno }}</h3>
                      <p class="text-sm text-gray-600 dark:text-gray-400">{{ usuario.email }}</p>
                    </div>
                    <RolUsuario 
                      :rol-id="usuario.rol?.id || 0"
                      :nombre="usuario.rol?.nombre || 'Sin rol'"
                    />
                  </div>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    @click="removeUsuario(usuario)"
                    class="text-red-500 hover:text-red-700 p-2 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Remover del negocio"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para asignar usuario -->
    <div v-if="showAddModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Asignar Usuario al Negocio
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <form @submit.prevent="asignarUsuario" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Usuario *
            </label>
            <select
              v-model="usuarioForm.usuario_id"
              required
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Selecciona un usuario</option>
              <option v-for="usuario in usuariosDisponibles" :key="usuario.id" :value="usuario.id">
                {{ usuario.nombre }} {{ usuario.apellidoPaterno }} - {{ usuario.email }}
              </option>
            </select>
          </div>

          <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p class="text-sm text-blue-700 dark:text-blue-300">
              <strong>Negocio:</strong> {{ selectedNegocio?.nombre }}
            </p>
          </div>

          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg v-if="loading" class="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ loading ? 'Asignando...' : 'Asignar Usuario' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp'
import RolUsuario from '@/components/RolUsuario.vue'
import Swal from 'sweetalert2'

// Referencias reactivas
const negocios = ref([])
const usuarios = ref([])
const usuariosAsignados = ref([])
const selectedNegocio = ref(null)
const negocioSearch = ref('')
const showAddModal = ref(false)
const usuarioForm = ref({
  usuario_id: ''
})

// Estados de carga
const loadingNegocios = ref(false)
const loadingUsuarios = ref(false)

// Composable HTTP
const { get, post, del, loading, error, data } = useHttp()

// Computed para filtrar negocios
const filteredNegocios = computed(() => {
  if (!negocioSearch.value) return negocios.value
  return negocios.value.filter(negocio =>
    negocio.nombre.toLowerCase().includes(negocioSearch.value.toLowerCase())
  )
})

// Usuarios disponibles (no asignados al negocio actual)
const usuariosDisponibles = computed(() => {
  if (!usuarios.value || !usuariosAsignados.value) return []
  const asignados = usuariosAsignados.value.map(u => u.id)
  return usuarios.value.filter(usuario => !asignados.includes(usuario.id))
})

// Funciones para cargar datos
const fetchNegocios = async () => {
  loadingNegocios.value = true
  try {
    await get('negocios')
    negocios.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar negocios:', err)
    negocios.value = []
  } finally {
    loadingNegocios.value = false
  }
}

const fetchUsuarios = async () => {
  try {
    await get('users')
    usuarios.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar usuarios:', err)
    usuarios.value = []
  }
}

const fetchUsuariosAsignados = async () => {
  if (!selectedNegocio.value) return
  
  loadingUsuarios.value = true
  try {
    await get(`negocios/${selectedNegocio.value.id}/usuarios`)
    usuariosAsignados.value = Array.isArray(data.value) ? data.value : data.value?.data || []
  } catch (err) {
    console.error('Error al cargar usuarios asignados:', err)
    usuariosAsignados.value = []
  } finally {
    loadingUsuarios.value = false
  }
}

// Función para seleccionar un negocio
const selectNegocio = (negocio) => {
  selectedNegocio.value = negocio
  fetchUsuariosAsignados()
}

// Funciones del modal
const closeModal = () => {
  showAddModal.value = false
  usuarioForm.value = {
    usuario_id: ''
  }
}

const asignarUsuario = async () => {
  try {
    const payload = {
      usuario_id: usuarioForm.value.usuario_id,
      negocio_id: selectedNegocio.value.id
    }
    
    await post('usuario-negocio', payload)
    closeModal()
    await fetchUsuariosAsignados()
    
    Swal.fire({
      icon: 'success',
      title: 'Usuario asignado',
      text: 'El usuario se asignó exitosamente al negocio.',
      timer: 2000,
      showConfirmButton: false
    })
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.value || 'Error al asignar el usuario al negocio'
    })
  }
}

const removeUsuario = async (usuario) => {
  const result = await Swal.fire({
    title: '¿Remover usuario?',
    text: `¿Seguro que deseas remover a ${usuario.nombre} ${usuario.apellidoPaterno} del negocio ${selectedNegocio.value.nombre}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, remover',
    cancelButtonText: 'Cancelar'
  })

  if (result.isConfirmed) {
    try {
      await del(`usuario-negocio/${usuario.id}/${selectedNegocio.value.id}`)
      await fetchUsuariosAsignados()
      
      Swal.fire({
        title: '¡Removido!',
        text: 'El usuario ha sido removido del negocio.',
        icon: 'success',
        timer: 1800,
        showConfirmButton: false
      })
    } catch (err) {
      Swal.fire('Error', 'No se pudo remover el usuario del negocio.', 'error')
    }
  }
}


// Cargar datos al montar el componente
onMounted(() => {
  fetchNegocios()
  fetchUsuarios()
})
</script>